import React from "react";
import { block, CONTENT, Icon, RegisterButton, DownloadAppButton, MultilineText } from "./shared";

/* A single 9:16 production frame in the river. Frames are managed in the
   admin (Admin → Landing → River): kind picks the abstract rendering,
   an attached image turns the frame into a still. */
function FrameCard({ f, eager }) {
  const hasImage = Boolean(f.image);
  const onDark = f.kind === "cut" || f.kind === "animatic" || hasImage;
  const label = { position: "absolute", top: 8, left: 8, fontFamily: "var(--font-mono)", fontSize: 10.5, fontWeight: 600, letterSpacing: "0.03em", padding: "3px 8px", borderRadius: "var(--radius-pill)", zIndex: 2, background: onDark ? "rgba(44,43,38,0.5)" : "var(--frame-label-bg)", color: onDark ? "var(--clay-0)" : "var(--frame-label-fg)", backdropFilter: onDark ? "blur(4px)" : "none" };
  const foot = { position: "absolute", bottom: 9, left: 10, right: 10, fontFamily: "var(--font-mono)", fontSize: 10, zIndex: 2, display: "flex", justifyContent: "space-between", color: onDark ? "rgba(250,246,238,0.88)" : "var(--text-faint)" };

  let bg = "var(--surface-card)", body = null;
  if (hasImage) {
    body = (
      <React.Fragment>
        <img src={f.image} alt="" loading={eager ? "eager" : "lazy"} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(44,43,38,0.18) 0%, transparent 36%, transparent 58%, rgba(44,43,38,0.55) 100%)" }}></div>
      </React.Fragment>
    );
  } else if (f.kind === "logline") {
    body = (
      <div style={{ padding: "32px 16px 26px", display: "grid", gap: 7 }}>
        <div style={{ height: 7, width: "58%", borderRadius: 4, background: "var(--accent-tint)" }}></div>
        {[92, 80, 86, 72, 88, 66, 78].map((w, i) => (
          <div key={i} style={{ height: 4.5, width: w + "%", borderRadius: 3, background: "var(--border-strong)" }}></div>
        ))}
      </div>
    );
  } else if (f.kind === "storyboard") {
    bg = "var(--bg-sunken)";
    body = (
      <div style={{ position: "absolute", inset: 0 }}>
        {["33.3%", "66.6%"].map((l) => <span key={l} style={{ position: "absolute", left: l, top: 0, bottom: 0, width: 1, background: "var(--clay-4)", opacity: 0.6 }}></span>)}
        {["33.3%", "66.6%"].map((t) => <span key={t} style={{ position: "absolute", top: t, left: 0, right: 0, height: 1, background: "var(--clay-4)", opacity: 0.6 }}></span>)}
        <span style={{ position: "absolute", left: "50%", top: "40%", transform: "translate(-50%,-50%)", width: 56, height: 56, borderRadius: "50%", border: "2px solid var(--clay-5)" }}></span>
        <span style={{ position: "absolute", left: "26%", bottom: "20%", width: "48%", height: 2, borderRadius: 2, background: "var(--clay-5)", opacity: 0.7 }}></span>
      </div>
    );
  } else if (f.kind === "previz") {
    bg = "linear-gradient(155deg, var(--sage-3), var(--river-4))";
    body = (
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(250,246,238,0.94)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "var(--shadow-sm)" }}>
          <Icon name="play" size={20} color="var(--accent)" />
        </span>
      </div>
    );
  } else if (f.kind === "animatic") {
    bg = "var(--clay-9)";
    const bars = [38, 60, 30, 78, 52, 88, 44, 66, 34, 72, 48, 58];
    body = (
      <div style={{ position: "absolute", inset: 0 }}>
        <span style={{ position: "absolute", left: 12, top: 30, width: 1, bottom: 54, background: "rgba(243,221,210,0.35)" }}></span>
        <div style={{ position: "absolute", left: 12, right: 12, bottom: 42, display: "flex", alignItems: "flex-end", gap: 3, height: 64 }}>
          {bars.map((h, i) => <span key={i} style={{ flex: 1, height: h + "%", background: "var(--ochre-2)", borderRadius: 2, opacity: 0.85 }}></span>)}
        </div>
      </div>
    );
  } else if (f.kind === "cut") {
    bg = "linear-gradient(150deg, var(--clay-8), var(--clay-9))";
    body = (
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name="clapperboard" size={26} color="var(--clay-6)" />
      </div>
    );
  }

  return (
    <div className={"riv-card" + (f.shimmer ? " riv-shimmer" : "")} style={{ flex: "none", width: f.wide ? 500 : 158, height: 281, borderRadius: "var(--radius-lg)", overflow: "hidden", position: "relative", background: bg, boxShadow: "var(--shadow-md), var(--shadow-clay-inset)", transform: `translateY(${f.y || 0}px) rotate(${f.r || 0}deg)` }}>
      {body}
      <span style={label}>{f.label}</span>
      <div style={foot}><span>{f.shot}</span>{f.tag && <span>{f.tag}</span>}</div>
    </div>
  );
}

/* The Frameriver: a full-screen stream of production frames maturing
   through the pipeline. Copy comes from the "hero" block. */
export function Hero() {
  const b = block("hero") || {};
  const river = CONTENT.river || [];
  const laneA = [...river, ...river];
  const half = Math.floor(river.length / 3);
  const offsetRiver = [...river.slice(half), ...river.slice(0, half)];
  const laneB = [...offsetRiver, ...offsetRiver];

  return (
    <section id="top" className="fr-hero fr-hero-river" style={{ position: "relative", overflow: "hidden", background: "var(--bg-app)", height: "calc(100svh - 60px)", minHeight: 560 }} data-screen-label="Hero">
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(var(--dot-color) 1px, transparent 1.4px)", backgroundSize: "26px 26px", opacity: 0.3 }}></div>

      {river.length > 0 && (
        <div className="riv-band" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%) rotate(-3.5deg)", display: "flex", flexDirection: "column", gap: 20 }}>
          <div className="riv-track a" style={{ display: "flex", gap: 18, alignItems: "center" }}>
            {laneA.map((f, i) => <FrameCard key={i} f={f} eager={i < 6} />)}
          </div>
          <div className="riv-track b" style={{ display: "flex", gap: 18, alignItems: "center" }}>
            {laneB.map((f, i) => <FrameCard key={i} f={f} eager={i < 6} />)}
          </div>
        </div>
      )}

      {/* edge fades + bottom key-art scrim */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(90deg, var(--bg-app) 0%, transparent 13%, transparent 87%, var(--bg-app) 100%)" }}></div>
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(180deg, var(--bg-app) 0%, transparent 16%)" }}></div>
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(0deg, var(--bg-app) 6%, rgb(var(--bg-app-rgb) / 0.9) 28%, rgb(var(--bg-app-rgb) / 0) 60%)" }}></div>
      {/* localized backing so the poster text stays legible over any frame */}
      <div aria-hidden="true" style={{ position: "absolute", left: 0, bottom: 0, width: "min(920px, 82%)", height: "74%", zIndex: 1, pointerEvents: "none", background: "radial-gradient(120% 96% at 12% 90%, rgb(var(--bg-app-rgb) / 0.96) 0%, rgb(var(--bg-app-rgb) / 0.72) 40%, rgb(var(--bg-app-rgb) / 0) 72%)" }}></div>

      {/* dark theme: the app's violet stage light over the river */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "var(--hero-glow)" }}></div>

      {/* KEY-ART text composition — anchored bottom-left, poster scale */}
      <div className="riv-keyart" style={{ position: "absolute", left: 0, right: 0, bottom: "clamp(34px,6vh,68px)", zIndex: 3, pointerEvents: "none" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 clamp(22px,5vw,56px)" }}>
          <div style={{ maxWidth: 860, pointerEvents: "auto" }}>
            <h1 style={{ fontSize: "clamp(36px, 5vw, 66px)", fontWeight: 700, color: "var(--text-display)", margin: 0, lineHeight: 1.04, letterSpacing: "-0.03em", textWrap: "balance", textShadow: "var(--hero-text-shadow)" }}>
              <MultilineText text={b.title} />
            </h1>
            <p style={{ fontSize: "clamp(15px, 1.35vw, 18px)", maxWidth: 560, margin: "20px 0 0", lineHeight: 1.55, color: "var(--text-body)" }}>
              {b.subtitle}
            </p>
            <div style={{ marginTop: 26, display: "flex", flexWrap: "wrap", gap: 12 }}>
              <RegisterButton label={b.cta_label} url={b.cta_url} />
              <DownloadAppButton />
            </div>
            {b.note && (
              <p style={{ fontSize: 13, color: "var(--text-faint)", margin: "13px 0 0" }}>
                {b.note}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── audiences / formats strip ────────────────────────────────── */
export function BuiltFor() {
  const b = block("built_for");
  if (!b) return null;
  return (
    <section id="built-for" style={{ maxWidth: 1180, margin: "0 auto", padding: "40px clamp(20px,5vw,56px) 8px", scrollMarginTop: 76 }}>
      <p className="reveal" style={{ textAlign: "center", margin: "0 0 18px", fontSize: 12.5, fontWeight: 600, letterSpacing: "var(--tracking-caps)", textTransform: "uppercase", color: "var(--text-faint)" }}>
        {b.title}
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10 }}>
        {(b.items || []).map((i, idx) => (
          <span key={i} className="reveal fr-lift" style={{ "--i": idx, fontFamily: "var(--font-body)", fontSize: 14, fontWeight: 600, color: "var(--text-muted)", padding: "8px 16px", borderRadius: "var(--radius-pill)", background: "var(--surface-card)", border: "1px solid var(--border-soft)", boxShadow: "var(--shadow-clay-inset), var(--shadow-xs)", cursor: "default" }}>{i}</span>
        ))}
      </div>
    </section>
  );
}
